import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.4;
    mask-image: radial-gradient(circle at center, rgba(0, 0, 0, 0.95), transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 30%, rgba(34, 211, 238, 0.18), transparent 25%),
      radial-gradient(circle at 85% 20%, rgba(167, 139, 250, 0.22), transparent 28%),
      radial-gradient(circle at 70% 80%, rgba(56, 189, 248, 0.18), transparent 30%);
    filter: blur(20px);
    opacity: 0.8;
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
  }
`;

const Shell = styled.div`
  width: min(1160px, 100%);
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: ${theme.spacing['2xl']};
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.div`
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.92), rgba(4, 8, 22, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.02);
  border-radius: ${theme.borderRadius['3xl']};
  padding: ${theme.spacing['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle at 30% 30%, rgba(34, 211, 238, 0.14), transparent 45%),
      radial-gradient(circle at 70% 60%, rgba(167, 139, 250, 0.18), transparent 50%);
    filter: blur(18px);
    opacity: 0.9;
    pointer-events: none;
  }
`;

const InfoContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Badge = styled.span`
  align-self: flex-start;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-family: ${theme.fonts.heading};
  font-weight: ${theme.fontWeights.bold};
  font-size: clamp(${theme.fontSizes['2xl']}, 3vw, ${theme.fontSizes['4xl']});
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.md};
  line-height: 1.6;
  max-width: 34ch;
`;

const Highlight = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const HighlightItem = styled.div`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.xl};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
`;

const HighlightTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing.xs};
`;

const HighlightText = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  margin: 0;
`;

const Card = styled.div`
  background: linear-gradient(170deg, rgba(15, 23, 42, 0.94), rgba(5, 8, 22, 0.94));
  border-radius: ${theme.borderRadius['3xl']};
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.03);
  padding: ${theme.spacing['2xl']};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  width: 100%;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xl};
  }
  
  @media (min-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing['3xl']};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: 0;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const LogoIcon = styled.div`
  width: 52px;
  height: 52px;
  background: conic-gradient(from 180deg at 50% 50%, rgba(34, 211, 238, 0.8) 0deg, rgba(167, 139, 250, 0.9) 120deg, rgba(34, 211, 238, 0.8) 240deg, rgba(34, 211, 238, 0.8) 360deg);
  border-radius: ${theme.borderRadius['2xl']};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textPrimary};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  box-shadow: 0 10px 40px rgba(34, 211, 238, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.16);
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Brand = styled.h1`
  font-size: ${theme.fontSizes['xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const BrandSub = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Pill = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.textPrimary};
  background: rgba(34, 211, 238, 0.14);
  border: 1px solid rgba(34, 211, 238, 0.35);
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  letter-spacing: 0.04em;
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, title, subtitle }) => {
  return (
    <LayoutContainer>
      <Shell>
        <InfoPanel className="slide-in">
          <InfoContent>
            <Badge>AI Workbench</Badge>
            <HeroTitle>One workspace for every ZJULAB AI product.</HeroTitle>
            <HeroSubtitle>
              Launch copilots, domain apps, and curated datasets from a single surface. Move from idea to
              production with shared settings, roles, and observability baked in.
            </HeroSubtitle>
            <Highlight>
              <HighlightItem>
                <HighlightTitle>Launch faster</HighlightTitle>
                <HighlightText>Discover and run AI apps, workflows, and notebooks without context switching.</HighlightText>
              </HighlightItem>
              <HighlightItem>
                <HighlightTitle>Operate together</HighlightTitle>
                <HighlightText>Shared identity, workspace presets, and team roles travel with every experience.</HighlightText>
              </HighlightItem>
              <HighlightItem>
                <HighlightTitle>Stay in control</HighlightTitle>
                <HighlightText>Usage insights, quotas, and audit trails keep governance clear for every team.</HighlightText>
              </HighlightItem>
            </Highlight>
          </InfoContent>
        </InfoPanel>

        <Card className="fade-in">
          <Header>
            <Logo>
              <LogoIcon>AI</LogoIcon>
              <LogoText>
                <Brand>ZJULAB AI Hub</Brand>
                <BrandSub>Unified Apps</BrandSub>
              </LogoText>
            </Logo>
            <Pill>Service Hub</Pill>
          </Header>
          <div>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
          {children}
        </Card>
      </Shell>
    </LayoutContainer>
  );
};
